import { useState } from 'react';
import type { MouseEvent } from 'react';

import { useTranslation } from 'react-i18next';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import {
  Chip,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  CompactTablePagination,
  EmptyState,
  ErrorState,
  LoadingState,
  TablePageShell,
} from '@/shared/ui';

import { useStock } from '../../hooks';
import { formatQuantity } from '../../lib/format';
import { useLocationLookup } from '../../lib/useLocationLookup';
import { LocationSelect } from './LocationSelect';
import { ProductIdCell } from './ProductIdCell';

export interface StockPanelProps {
  onOpenLedger: (productId: string, locationId: string) => void;
  onOpenPolicy: (productId: string, locationId: string) => void;
}

/** Tab 1 — current stock levels per (product, location), the default tab. */
export function StockPanel({ onOpenLedger, onOpenPolicy }: StockPanelProps) {
  const { t } = useTranslation('inventory');
  const { locations, getLocationName } = useLocationLookup();

  const [locationFilter, setLocationFilter] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<{ productId: string; locationId: string } | null>(null);

  const query = useStock({
    locationId: locationFilter || undefined,
    lowStockOnly,
    outOfStockOnly,
    page,
    size,
  });
  const items = query.data?.items ?? [];
  const total = query.data?.page?.totalElements ?? 0;
  const filtered = locationFilter !== '' || lowStockOnly || outOfStockOnly;

  const resetPageAnd = (fn: () => void) => {
    fn();
    setPage(0);
  };

  const openMenu = (
    event: MouseEvent<HTMLElement>,
    row: { productId: string; locationId: string },
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  return (
    <Stack spacing={2.5}>
      {/* Filter bar */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
        <LocationSelect
          label={t('stockPanel.locationFilterLabel')}
          value={locationFilter}
          onChange={(value) => resetPageAnd(() => setLocationFilter(value))}
          locations={locations}
          allOptionLabel={t('stockPanel.allLocations')}
          sx={{ minWidth: 220 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={lowStockOnly}
              onChange={(e) => resetPageAnd(() => setLowStockOnly(e.target.checked))}
            />
          }
          label={t('stockPanel.lowStockOnly')}
        />
        <FormControlLabel
          control={
            <Switch
              checked={outOfStockOnly}
              onChange={(e) => resetPageAnd(() => setOutOfStockOnly(e.target.checked))}
            />
          }
          label={t('stockPanel.outOfStockOnly')}
        />
      </Stack>

      {/* Content states */}
      {query.isLoading ? (
        <LoadingState label={t('stockPanel.loading')} />
      ) : query.isError ? (
        <ErrorState
          title={t('stockPanel.loadError.title')}
          message={query.error?.message ?? t('common:error.unknown')}
          onRetry={() => void query.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={filtered ? t('common:empty.noResults') : t('stockPanel.empty.title')}
          description={
            filtered ? t('stockPanel.empty.filteredDescription') : t('stockPanel.empty.description')
          }
          icon={<Inventory2RoundedIcon />}
        />
      ) : (
        <TablePageShell
          footer={
            <CompactTablePagination
              count={total}
              page={page}
              rowsPerPage={size}
              rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(_e, next) => setPage(next)}
              onRowsPerPageChange={(e) => resetPageAnd(() => setSize(parseInt(e.target.value, 10)))}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('common:pagination.of')} ${count}`
              }
            />
          }
        >
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('stockPanel.table.product')}</TableCell>
                  <TableCell>{t('stockPanel.table.location')}</TableCell>
                  <TableCell align="right">{t('stockPanel.table.onHand')}</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {t('stockPanel.table.reserved')}
                  </TableCell>
                  <TableCell align="right">{t('stockPanel.table.available')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {t('stockPanel.table.uom')}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {t('stockPanel.table.backorder')}
                  </TableCell>
                  <TableCell align="right">{t('stockPanel.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <ProductIdCell productId={row.productId} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.locationId}>
                        <Typography variant="body2">{getLocationName(row.locationId)}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">{formatQuantity(row.onHandQty)}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {formatQuantity(row.reservedQty)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatQuantity(row.availableQty)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {row.uom}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={row.backorderAllowed ? 'success' : 'default'}
                        label={
                          row.backorderAllowed
                            ? t('stockPanel.backorderChip.allowed')
                            : t('stockPanel.backorderChip.notAllowed')
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('stockPanel.rowActions.menu')}>
                        <IconButton
                          size="small"
                          aria-label={t('stockPanel.rowActions.menuAria', {
                            productId: row.productId,
                          })}
                          onClick={(e) =>
                            openMenu(e, { productId: row.productId, locationId: row.locationId })
                          }
                        >
                          <MoreVertRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TablePageShell>
      )}

      <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuRow) onOpenLedger(menuRow.productId, menuRow.locationId);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <HistoryRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('stockPanel.rowActions.ledger')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuRow) onOpenPolicy(menuRow.productId, menuRow.locationId);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <TuneRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('stockPanel.rowActions.policy')}</ListItemText>
        </MenuItem>
      </Menu>
    </Stack>
  );
}
